import { useContext, useMemo } from 'react'
import { AppContext, AppContextType, AudioEngineDevSettings } from '@/AppContext'
import ElectronIPC from '@/services/ElectronIPC'
import { persistent } from '@/services/LocalStorage'
import { getAudioEngineInstance } from '@/core/AudioEngine'
import { Button } from '@/UIComponents2/Button'
import { DialogBody } from '@/components/Dialog'
import { TextInput } from '@/UIComponents2/TextInput'

// isLogToFileEnabled
// logOutputDirectory
// textLogFileName
// inputDeviceFramesPerBufferOverride
// outputDeviceFramesPerBufferOverride
// isAudioLogSender0Enabled
// isAudioLogSender1Enabled
// isAudioLogReceiver0Enabled
// isAudioLogReceiver1Enabled
// isAudioLogReceiver2Enabled

export default function AudioEngineDevSettingsPage() {
    const { state: appState } = useContext(AppContext) as AppContextType

    const settings = appState.appSettings.audioEngineDevSettings

    const onApply = () => {
        getAudioEngineInstance().setSettings(settings)
    }

    return (
        <DialogBody>
            <div className="grid grid-cols-2 gap-2 items-center">
                <BooleanSettingLine
                    title="Log to file enabled"
                    subtitle="(isLogToFileEnabled)"
                    selector="isLogToFileEnabled"
                />

                <LogLevelLine />

                <ReadOnlyDirectorySettingLine path={settings.logOutputDirectory} />

                <RelayServerUrlLine />

                <InputDeviceFramesPerBufferLine />

                <OutputDeviceFramesPerBufferLine />

                <BooleanSettingLine
                    title="Audio log checkpoint sender 0"
                    subtitle="(isAudioLogSender0Enabled)"
                    selector="isAudioLogSender0Enabled"
                />
                <BooleanSettingLine
                    title="Audio log checkpoint sender 1"
                    subtitle="(isAudioLogSender1Enabled)"
                    selector="isAudioLogSender1Enabled"
                />
                <BooleanSettingLine
                    title="Audio log checkpoint receiver 0"
                    subtitle="(isAudioLogReceiver0Enabled)"
                    selector="isAudioLogReceiver0Enabled"
                />
                <BooleanSettingLine
                    title="Audio log checkpoint receiver 1"
                    subtitle="(isAudioLogReceiver1Enabled)"
                    selector="isAudioLogReceiver1Enabled"
                />
                <BooleanSettingLine
                    title="Audio log checkpoint receiver 2"
                    subtitle="(isAudioLogReceiver2Enabled)"
                    selector="isAudioLogReceiver2Enabled"
                />
                <BooleanSettingLine
                    title="Audio log checkpoint primary output"
                    subtitle="(isAudioLogPrimaryOutputEnabled)"
                    selector="isAudioLogPrimaryOutputEnabled"
                />
            </div>
            <Button variant="primary" className="mt-5" onClick={onApply}>
                Apply
            </Button>
        </DialogBody>
    )
}

function LogLevelLine() {
    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const settings = appState.appSettings.audioEngineDevSettings

    const onSelect = (value: 'verbose' | 'info') => {
        let next: AudioEngineDevSettings = {
            ...settings,
            logLevel: value,
        }

        appDispatch({
            type: 'update_app_settings',
            value: { audioEngineDevSettings: next },
        })
    }

    return (
        <>
            <div>
                <div>Log level</div>
            </div>
            <div>
                <select
                    value={settings.logLevel}
                    onChange={(ev) => {
                        if (ev.target.value === 'verbose' || ev.target.value === 'info') {
                            onSelect(ev.target.value)
                        }
                    }}
                >
                    <option value="verbose">verbose</option>
                    <option value="info">info</option>
                </select>
            </div>
        </>
    )
}

function ReadOnlyDirectorySettingLine({ path }: { path: string }) {
    return (
        <>
            <div>Log directory</div>
            <div className="flex flex-col gap-2">
                <input style={{ width: '100%' }} readOnly value={path} />
                <Button
                    variant="secondary"
                    onClick={() => {
                        ElectronIPC.openPath(path)
                    }}
                >
                    Open
                </Button>
            </div>
        </>
    )
}

function RelayServerUrlLine() {
    const onChange = (ev: any) => {
        const value = ev.target.value
        persistent.set('audioEngineRelayServerUrlOverride', value)
    }

    const initialValue = useMemo(() => {
        return persistent.getString('audioEngineRelayServerUrlOverride') || ''
    }, [])

    return (
        <>
            <div>
                <div>Relay server url (global override)</div>
            </div>
            <div>
                <TextInput initialValue={initialValue} onChange={onChange} />
            </div>
        </>
    )
}

function InputDeviceFramesPerBufferLine() {
    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const settings = appState.appSettings.audioEngineDevSettings

    const onChange = (ev: any) => {
        const str = (ev.target.value || '').trim()
        const value = str === '' ? 0 : parseInt(str)
        const next = {
            ...settings,
            inputDeviceFramesPerBufferOverride: value,
        }
        appDispatch({
            type: 'update_app_settings',
            value: { audioEngineDevSettings: next },
        })
    }

    const initialValue = settings.inputDeviceFramesPerBufferOverride

    return (
        <>
            <div>Input device frames per buffer</div>
            <TextInput type="number" initialValue={initialValue + ''} onChange={onChange} />
        </>
    )
}

function OutputDeviceFramesPerBufferLine() {
    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const settings = appState.appSettings.audioEngineDevSettings

    const onChange = (ev: any) => {
        const str = (ev.target.value || '').trim()
        const value = str === '' ? 0 : parseInt(str)
        const next = {
            ...settings,
            outputDeviceFramesPerBufferOverride: value,
        }
        appDispatch({
            type: 'update_app_settings',
            value: { audioEngineDevSettings: next },
        })
    }

    const initialValue = settings.outputDeviceFramesPerBufferOverride

    return (
        <>
            <div>Output device frames per buffer</div>
            <TextInput type="number" initialValue={initialValue + ''} onChange={onChange} />
        </>
    )
}

function BooleanSettingLine({
    title,
    subtitle,
    selector,
}: {
    title: string
    subtitle: string
    selector: keyof AudioEngineDevSettings
}) {
    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const settings = appState.appSettings.audioEngineDevSettings

    const toggle = (selector: keyof AudioEngineDevSettings) => {
        let next: AudioEngineDevSettings = { ...settings }

        // @ts-ignore
        next[selector] = !next[selector]

        appDispatch({
            type: 'update_app_settings',
            value: { audioEngineDevSettings: next },
        })
    }

    return (
        <>
            <div>
                <div>{title}</div>
                <div>{subtitle}</div>
            </div>
            <div>
                <input type="checkbox" checked={!!settings[selector]} onChange={() => toggle(selector)}></input>
            </div>
        </>
    )
}
